import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  FormControlLabel,
  Switch
} from '@mui/material';
import { motion } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { createAuthHeaders, handleTokenExpiration, isTokenExpired } from '../utils/auth';
import { useAuth } from '../context/AuthContext';

const steps = ['Basic Information', 'LiveKit Setup', 'Review & Create'];

const CreateAgent = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [llmModel, setLlmModel] = useState('gpt-4o');
  const [language, setLanguage] = useState('en-US');
  const [voice, setVoice] = useState('en-US-JennyNeural');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    livekit_url: '',
    api_key: '',
    api_secret: '',
    is_external: false
  });

  const languageOptions = [
    { value: 'en-US', label: 'English (US)' },
    { value: 'en-GB', label: 'English (UK)' },
    { value: 'en-AU', label: 'English (Australia)' },
    { value: 'es-ES', label: 'Spanish' },
    { value: 'fr-FR', label: 'French' },
    { value: 'de-DE', label: 'German' },
    { value: 'it-IT', label: 'Italian' },
    { value: 'pt-BR', label: 'Portuguese (Brazil)' },
    { value: 'nl-NL', label: 'Dutch' },
    { value: 'ja-JP', label: 'Japanese' },
    { value: 'ko-KR', label: 'Korean' },
    { value: 'zh-CN', label: 'Chinese (Simplified)' },
    { value: 'ru-RU', label: 'Russian' },
    { value: 'ar-SA', label: 'Arabic' }
  ];

  const modelOptions = [
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
    { value: 'gpt-3.5-turbo-16k', label: 'GPT-3.5 Turbo (16k)' },
    { value: 'gpt-4', label: 'GPT-4' },
    { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
    { value: 'gpt-4o', label: 'GPT-4o' },
    { value: 'gpt-4.5-preview', label: 'GPT-4.5 Preview' },
    { value: 'o1-preview', label: 'o1 Preview' },
    { value: 'o1-mini', label: 'o1 Mini' }
  ];

  const getVoiceOptions = (language) => {
    const voiceMap = {
      'en-US': [
        { value: 'en-US-JennyNeural', label: 'Jenny (Female)' },
        { value: 'en-US-GuyNeural', label: 'Guy (Male)' },
        { value: 'en-US-AriaNeural', label: 'Aria (Female)' },
        { value: 'en-US-AmberNeural', label: 'Amber (Female)' }
      ],
      'en-GB': [
        { value: 'en-GB-LibbyNeural', label: 'Libby (Female)' },
        { value: 'en-GB-RyanNeural', label: 'Ryan (Male)' },
        { value: 'en-GB-MiaNeural', label: 'Mia (Female)' }
      ],
      'en-AU': [
        { value: 'en-AU-NatashaNeural', label: 'Natasha (Female)' },
        { value: 'en-AU-WilliamNeural', label: 'William (Male)' }
      ],
      'fr-FR': [
        { value: 'fr-FR-DeniseNeural', label: 'Denise (Female)' },
        { value: 'fr-FR-HenriNeural', label: 'Henri (Male)' }
      ],
      'de-DE': [
        { value: 'de-DE-KatjaNeural', label: 'Katja (Female)' },
        { value: 'de-DE-ConradNeural', label: 'Conrad (Male)' }
      ],
      'es-ES': [
        { value: 'es-ES-ElviraNeural', label: 'Elvira (Female)' },
        { value: 'es-ES-AlvaroNeural', label: 'Alvaro (Male)' }
      ],
      'it-IT': [
        { value: 'it-IT-IsabellaNeural', label: 'Isabella (Female)' },
        { value: 'it-IT-DiegoNeural', label: 'Diego (Male)' }
      ],
      'ja-JP': [
        { value: 'ja-JP-NanamiNeural', label: 'Nanami (Female)' },
        { value: 'ja-JP-KeitaNeural', label: 'Keita (Male)' }
      ],
      'zh-CN': [
        { value: 'zh-CN-XiaoxiaoNeural', label: 'Xiaoxiao (Female)' },
        { value: 'zh-CN-YunjianNeural', label: 'Yunjian (Male)' }
      ],
      'pt-BR': [
        { value: 'pt-BR-FranciscaNeural', label: 'Francisca (Female)' },
        { value: 'pt-BR-AntonioNeural', label: 'Antonio (Male)' }
      ],
      'nl-NL': [
        { value: 'nl-NL-FennaNeural', label: 'Fenna (Female)' },
        { value: 'nl-NL-MaartenNeural', label: 'Maarten (Male)' }
      ],
      'ru-RU': [
        { value: 'ru-RU-SvetlanaNeural', label: 'Svetlana (Female)' },
        { value: 'ru-RU-DmitryNeural', label: 'Dmitry (Male)' }
      ],
      'ko-KR': [
        { value: 'ko-KR-SunHiNeural', label: 'SunHi (Female)' },
        { value: 'ko-KR-InJoonNeural', label: 'InJoon (Male)' }
      ],
      'ar-SA': [
        { value: 'ar-SA-ZariyahNeural', label: 'Zariyah (Female)' },
        { value: 'ar-SA-HamedNeural', label: 'Hamed (Male)' }
      ]
    };

    return voiceMap[language] || voiceMap['en-US'];
  };

  const validateStep = (step) => {
    switch (step) {
      case 0:
        return formData.name.length >= 3;
      case 1:
        return (
          formData.livekit_url.startsWith('wss://') &&
          formData.api_key.length > 0 &&
          formData.api_secret.length > 0
        );
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
      setError('');
    } else {
      setError('Please fill in all required fields correctly');
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setError('');
  };

  // Update voice when language changes
  const handleLanguageChange = (e) => {
    const newLanguage = e.target.value;
    setLanguage(newLanguage);
    
    const voices = getVoiceOptions(newLanguage);
    if (voices.length > 0) {
      setVoice(voices[0].value);
    }
  };

  const getLiveKitInstructions = () => (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        LiveKit Project Setup
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Follow these steps to create a LiveKit project and get your credentials:
      </Typography>
      
      <List>
        <ListItem>
          <ListItemIcon>
            <ArrowRightIcon color="primary" />
          </ListItemIcon>
          <ListItemText 
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Go to LiveKit Cloud Console
                <Link 
                  href="https://cloud.livekit.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <OpenInNewIcon sx={{ fontSize: 16, ml: 0.5 }} />
                </Link>
              </Box>
            }
            secondary="Sign in or create an account if you haven't already"
          />
        </ListItem>
        
        <ListItem>
          <ListItemIcon>
            <ArrowRightIcon color="primary" />
          </ListItemIcon>
          <ListItemText 
            primary="Create a New Project"
            secondary="Click 'New Project' and choose a name for your agent"
          />
        </ListItem>

        <ListItem>
          <ListItemIcon>
            <ArrowRightIcon color="primary" />
          </ListItemIcon>
          <ListItemText 
            primary="Go to Project Settings"
            secondary="Navigate to the Settings tab in your project"
          />
        </ListItem>

        <ListItem>
          <ListItemIcon>
            <ArrowRightIcon color="primary" />
          </ListItemIcon>
          <ListItemText 
            primary="Copy Project URL"
            secondary="Find your project URL (looks like wss://your-project.livekit.cloud)"
          />
        </ListItem>

        <ListItem>
          <ListItemIcon>
            <ArrowRightIcon color="primary" />
          </ListItemIcon>
          <ListItemText 
            primary="Generate API Keys"
            secondary="In the Keys section, create a new API key pair specifically for this agent"
          />
        </ListItem>
      </List>

      <Alert severity="info" sx={{ mt: 2 }}>
        Make sure to save your API Secret immediately after generation - it won't be shown again!
      </Alert>

      <Box sx={{ mt: 3 }}>
        <TextField
          fullWidth
          label="LiveKit Project URL"
          placeholder="wss://your-project.livekit.cloud"
          value={formData.livekit_url}
          onChange={(e) => setFormData({ ...formData, livekit_url: e.target.value })}
          margin="normal"
          helperText="The WebSocket URL for your LiveKit project"
        />
        <TextField
          fullWidth
          label="API Key"
          value={formData.api_key}
          onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
          margin="normal"
          helperText="Your LiveKit API Key ID"
        />
        <TextField
          fullWidth
          label="API Secret"
          value={formData.api_secret}
          onChange={(e) => setFormData({ ...formData, api_secret: e.target.value })}
          margin="normal"
          type="password"
          helperText="Your LiveKit API Secret (keep this secure!)"
        />
      </Box>
    </Box>
  );

  const getStepContent = (step) => {
    if (success) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Agent Created Successfully!
          </Typography>
          <Typography color="text.secondary">
            Redirecting to agents page...
          </Typography>
        </Box>
      );
    }

    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Give your agent a name and description. This will help you identify it later.
            </Typography>
            <TextField
              fullWidth
              label="Agent Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              helperText="A unique name for your agent"
            />
            
            <Box sx={{ mt: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_external}
                    onChange={(e) => setFormData({ ...formData, is_external: e.target.checked })}
                  />
                }
                label="External Agent"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                External agents have limited configuration options and are primarily used for third-party integrations.
                Internal agents (default) provide full configuration control.
              </Typography>
            </Box>
          </Box>
        );
      case 1:
        return getLiveKitInstructions();
      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Information
            </Typography>
            <Paper sx={{ p: 3, mt: 2 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Agent Name
                </Typography>
                <Typography variant="body1">{formData.name}</Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Agent Type
                </Typography>
                <Typography variant="body1">
                  {formData.is_external ? 'External (Limited Configuration)' : 'Internal (Full Configuration)'}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  LiveKit Project URL
                </Typography>
                <Typography variant="body1">{formData.livekit_url || 'Not provided'}</Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  API Configuration
                </Typography>
                <Typography variant="body1">
                  API Key: {formData.api_key || 'Not provided'}
                </Typography>
                <Typography variant="body1">
                  API Secret: {formData.api_secret ? '••••••••••••••••' : 'Not provided'}
                </Typography>
              </Box>
            </Paper>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter an agent name',
        severity: 'error'
      });
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/agent-creation/create`, {
        method: 'POST',
        headers: createAuthHeaders(),
        body: JSON.stringify({
          name: name.trim(),
          agent_type: 'inbound',
          prompt_text: 'You are a helpful assistant.',
          initial_message: 'Hello, how can I help you today?',
          language: language,
          voice: voice,
          llm_model: llmModel,
          owner_id: user?.id
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401 && isTokenExpired(data)) {
          handleTokenExpiration();
          return;
        }
        throw new Error(data.error || 'Failed to create agent');
      }

      setSnackbar({
        open: true,
        message: 'Inbound agent created successfully',
        severity: 'success'
      });

      setTimeout(() => {
        navigate('/agents');
      }, 1500);
    } catch (error) {
      console.error('Error creating agent:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to create agent',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    
    try {
      const agentData = {
        ...formData,
        owner_id: user?.id
      };
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/agent-creation/create`, {
        method: 'POST',
        headers: createAuthHeaders(),
        body: JSON.stringify(agentData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        if (response.status === 401 && isTokenExpired(data)) {
          handleTokenExpiration();
          return;
        }
        throw new Error(data.error || 'Failed to create agent');
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/agents');
      }, 3000);
    } catch (error) {
      console.error('Error creating agent:', error);
      setError(error.message);
      setActiveStep(2); // Stay on review step
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Create New Agent
      </Typography>
      
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Paper sx={{ p: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {getStepContent(activeStep)}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            {activeStep !== 0 && !success && (
              <Button 
                onClick={handleBack} 
                sx={{ mr: 1 }}
                disabled={saving}
              >
                Back
              </Button>
            )}
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={saving || !validateStep(activeStep)}
                startIcon={saving && <CircularProgress size={20} />}
              >
                {saving ? 'Creating...' : 'Create Agent'}
              </Button>
            ) : (
              <Button 
                variant="contained" 
                onClick={handleNext}
                disabled={!validateStep(activeStep)}
              >
                Next
              </Button>
            )}
          </Box>
        </Paper>
      </motion.div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateAgent;